import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from 'react-router';
import { Typography, useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';
import { Button } from '../../components/button/index.ts';
import { Path } from '../../assets/enums/index.ts';
import { BackIcon } from '../../assets/icons/back.tsx';
import {
  getApiErrorMessage,
  getApiErrorStatusCode,
  type ApiErrorPageEventDetail,
} from '../../api/response-errors.ts';
import { useAppNotify } from '../../assets/helpers/hooks/index.ts';
import React from 'react';
import { ScrollBox } from '@src/features/index.ts';

const CLIENT_ERROR_CODE = 'CLIENT_ERROR';

const getRouteErrorStatusCode = (error: unknown) => {
  if (isRouteErrorResponse(error)) {
    return error.status;
  }

  return getApiErrorStatusCode(error);
};

const getRouteErrorMessage = (error: unknown) => {
  if (isRouteErrorResponse(error)) {
    return typeof error.data === 'string' ? error.data : error.statusText;
  }

  if (error) {
    return getApiErrorMessage(error);
  }

  return undefined;
};

type ErrorPageProps = {
  errorState?: ApiErrorPageEventDetail;
  onGoHome?: () => void;
};

export const ErrorPage = ({ errorState: apiErrorState, onGoHome }: ErrorPageProps) => {
  const { palette } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const routeError = useRouteError();
  const { notifyError } = useAppNotify();
  const errorState = apiErrorState ?? (location.state as ApiErrorPageEventDetail | null);
  const statusCode = errorState?.statusCode ?? getRouteErrorStatusCode(routeError) ?? CLIENT_ERROR_CODE;
  const errorMessage = errorState?.message ?? getRouteErrorMessage(routeError);
  const displayMessage = errorMessage || 'The page with this address does not exist.';
  const handleGoHome = () => {
    onGoHome?.();
    navigate(Path.Home);
  };

  React.useEffect(() => {
    notifyError(displayMessage, {
      title: `Error ${statusCode}`,
    });
  }, [displayMessage, notifyError, statusCode]);

  return (
    <Stack sx={{
      height: 'calc(100dvh - var(--app-main-mt) - var(--app-main-pb))',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Stack gap={6} alignItems={'center'}>

        <Typography variant="giant" color={palette.accent}>{statusCode}</Typography>
        <Typography textAlign={'center'} variant={'body_14_24'} color={palette.dark}>
          Something went wrong..
        </Typography>
        <ScrollBox sx={{ maxHeight: '300px', textAlign: 'center' }}>
          <Typography textAlign={'center'} variant={'body_14_24'} color={palette.dark}>
            {displayMessage}
          </Typography>
        </ScrollBox>

        <Button variantStyle={'regular'} fullWidth={false} onClick={handleGoHome}>
          Go to main page
          <BackIcon />
        </Button>
      </Stack>
    </Stack>
  );
};
